import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Loader2, CheckCircle2, XCircle, Plus, History, Copy, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import YouTubePlayer from './YouTubePlayer';
import NovaActionZone from './NovaActionZone';
import TroubleshootingControls, { TroubleshootingProgress } from './TroubleshootingFlow';
import VideoValidation from './VideoValidation';

const NOVA_AVATAR = '/nova-avatar.jpg';

const WELCOME = "Bonjour, je suis Nova, votre assistante Support IPTV4KFR.\nEn quoi puis-je vous aider aujourd'hui ?";
const SUGGESTIONS = [
  'Installer une application',
  'Entrer mes identifiants',
  'Les chaînes ne fonctionnent pas',
  'Problème de coupures ou de buffering',
  'Problème avec la VOD ou les séries',
  'Mon abonnement ne fonctionne plus',
  'Autre problème',
];

const NovaAvatar = ({ size = 32 }) => (
  <img
    src={NOVA_AVATAR}
    alt="Nova"
    style={{ width: size, height: size }}
    className="rounded-full object-cover border border-[#FF6B35]/40 flex-shrink-0"
  />
);

// Rendu léger : gras **texte** + retours à la ligne.
const renderRich = (text) => {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g).map((p, j) =>
      p.startsWith('**') && p.endsWith('**')
        ? <strong key={j} className="text-white">{p.slice(2, -2)}</strong>
        : <span key={j}>{p}</span>
    );
    return <div key={i} className={line.trim() === '' ? 'h-2' : 'mb-1'}>{parts}</div>;
  });
};

const welcomeMessage = () => ({ role: 'assistant', content: WELCOME, quick_replies: SUGGESTIONS });

export const AssistantChat = ({ initialQuestion = '', pageContext = null, onNavigate = null, initialAssistant = null, compact = false }) => {
  const buildWelcome = useCallback(() => (initialAssistant || welcomeMessage()), [initialAssistant]);
  const [messages, setMessages] = useState([initialAssistant || welcomeMessage()]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [thinkingVideo, setThinkingVideo] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [detected, setDetected] = useState({});
  const [ytConfigured, setYtConfigured] = useState(true);
  const [history, setHistory] = useState([]);
  const [showHistoryMobile, setShowHistoryMobile] = useState(false);
  const [openSourcesIdx, setOpenSourcesIdx] = useState(null);
  // Dépannage guidé (Phase 4.3)
  const [tsSession, setTsSession] = useState(null);
  const [tsSessionId, setTsSessionId] = useState(null);
  const [tsLoading, setTsLoading] = useState(false);
  const [tsVideos, setTsVideos] = useState([]);
  const [activeSubId, setActiveSubId] = useState(null);
  const [pendingNewIssueMsg, setPendingNewIssueMsg] = useState(null);
  const [showNewIssueConfirm, setShowNewIssueConfirm] = useState(false);
  // Reprise des diagnostics (Part 2 — jalon consolidation)
  const [recentSessions, setRecentSessions] = useState([]);
  const [showRecent, setShowRecent] = useState(false);
  const [resumableReminder, setResumableReminder] = useState(null); // {session_id, problem,...}
  const [resumeConflict, setResumeConflict] = useState(null); // session cible à reprendre malgré une session active
  const scrollRef = useRef(null);
  const startedRef = useRef(false);
  const resumedRef = useRef(false);
  const navigate = useNavigate();

  const isLoggedIn = !!localStorage.getItem('auth_token');

  const campaignSource = (pageContext && pageContext.campaign_source) || sessionStorage.getItem('nova_campaign_source') || null;
  const novaStartedRef = useRef(false);
  const emitCampaign = useCallback((event, extra = {}) => {
    if (!campaignSource) return;
    api.post('/assistant/campaign-event', { event, campaign: campaignSource, ...extra }).catch(() => {});
  }, [campaignSource]);

  const handleNavigate = useCallback((path) => {
    if (path === '/connexion' || path === '/inscription') {
      const lastUser = [...messages].reverse().find((m) => m.role === 'user');
      if (lastUser) sessionStorage.setItem('nova_pending_intent', lastUser.content);
    }
    navigate(path);
    if (onNavigate) onNavigate(path);
  }, [navigate, onNavigate, messages]);

  const loadHistory = useCallback(() => {
    if (!isLoggedIn) return;
    api.get('/assistant/conversations').then((r) => setHistory(r.data)).catch(() => {});
  }, [isLoggedIn]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  useEffect(() => {
    // Défilement interne au conteneur (jamais la page). Pas de scroll sur l'accueil seul.
    if (messages.length > 1 || loading) {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, [messages, loading]);

  // Statut "recherche vidéo" après quelques secondes de chargement
  useEffect(() => {
    if (!loading) { setThinkingVideo(false); return; }
    const t = setTimeout(() => setThinkingVideo(true), 4000);
    return () => clearTimeout(t);
  }, [loading]);

  const applyTsPayload = useCallback((d, { pushMessage = true } = {}) => {
    if (pushMessage && d.nova_message) {
      setMessages((m) => [...m, { role: 'assistant', content: d.nova_message }]);
    }
    setTsSession(d);
    setTsSessionId(d.session_id);
    setTsVideos([]);
    // Suivi campagne : test de lecture atteint
    if (campaignSource && d.phase && /test/i.test(d.phase)) emitCampaign('playback_test');
    if (d.terminal) {
      localStorage.removeItem('nova_ts_session');
      loadHistory();
    } else if (d.session_id) {
      localStorage.setItem('nova_ts_session', d.session_id);
    }
  }, [loadHistory, campaignSource, emitCampaign]);

  const tsAnswer = useCallback(async (value, freeText, pushUser = true, label = null, forceAnswer = false) => {
    // Suivi campagne : appareil / application sélectionné pendant la collecte
    if (campaignSource && tsSession && tsSession.is_context_question) {
      const f = tsSession.context_field;
      if (f === 'device' || f === 'known_device') emitCampaign('device_selected');
      else if (f === 'application') emitCampaign('app_selected');
    }
    if (pushUser) setMessages((m) => [...m, { role: 'user', content: label || freeText || value }]);
    setTsLoading(true);
    try {
      const r = await api.post(`/assistant/troubleshooting/${tsSessionId}/answer`,
        { value, free_text: freeText, force_answer: forceAnswer });
      // Détection d'un NOUVEAU problème pendant une session : on ne consomme pas la réponse,
      // on propose de basculer ou de continuer.
      if (r.data && r.data.new_issue_detected) {
        setPendingNewIssueMsg(r.data.pending_message);
        setMessages((m) => [...m, { role: 'assistant', content: r.data.nova_message, newIssueChoices: true }]);
        return;
      }
      applyTsPayload(r.data);
    } catch (e) {
      toast.error("Une erreur est survenue. Réessayez.");
    } finally {
      setTsLoading(false);
    }
  }, [tsSessionId, applyTsPayload, campaignSource, emitCampaign, tsSession]);

  const sendToConversation = useCallback(async (content) => {
    setLoading(true);
    try {
      const res = await api.post('/assistant/conversation', {
        message: content,
        conversation_id: conversationId,
        defer_video: true,
        page_context: pageContext || undefined,
      });
      const d = res.data;
      setConversationId(d.conversation_id);
      setDetected(d.detected || {});
      setYtConfigured(d.youtube_configured);
      setMessages((m) => [...m, {
        role: 'assistant',
        content: d.reply,
        videos: d.videos || [],
        quick_replies: d.quick_replies || [],
        awaiting_resolution: d.awaiting_resolution,
        is_question: d.is_question,
        action: d.action || null,
        videoLoading: !!d.pending_video,
        researched_answer_id: d.researched_answer_id || null,
        confidence_score: d.confidence_score || null,
        official_sources: d.official_sources && d.official_sources.show ? d.official_sources : null,
      }]);
      loadHistory();
      if (d.pending_video) fetchDeferredVideos(d.conversation_id);
    } catch (e) {
      toast.error("Une erreur est survenue. Réessayez.");
      setMessages((m) => [...m, { role: 'assistant', content: "Désolé, une erreur est survenue. Réessayez dans un instant." }]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, loadHistory, pageContext]);

  const startTroubleshooting = useCallback(async (content, { pushUser = false } = {}) => {
    if (pushUser) setMessages((m) => [...m, { role: 'user', content }]);
    setLoading(true);
    try {
      const res = await api.post('/assistant/troubleshooting/start', {
        message: content, conversation_id: conversationId, subscription_id: activeSubId || undefined,
      });
      if (res.data && res.data.redirect_to_conversation) {
        await sendToConversation(content);
      } else {
        if (campaignSource && !novaStartedRef.current) { novaStartedRef.current = true; emitCampaign('nova_started'); }
        applyTsPayload(res.data);
      }
    } catch (e) {
      await sendToConversation(content);
    } finally {
      setLoading(false);
    }
  }, [conversationId, activeSubId, sendToConversation, applyTsPayload, campaignSource, emitCampaign]);

  const sendMessage = useCallback(async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading || tsLoading) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content }]);
    // Réponse libre pendant un dépannage guidé en cours
    if (tsSession && !tsSession.terminal) {
      return tsAnswer(null, content, false);
    }
    await startTroubleshooting(content, { pushUser: false });
  }, [input, loading, tsLoading, tsSession, tsAnswer, startTroubleshooting]);

  // --- Nouveau problème : réinitialisation propre du dépannage guidé ---
  const resetTs = useCallback(() => {
    setTsSession(null); setTsSessionId(null); setTsVideos([]);
    setPendingNewIssueMsg(null); setShowNewIssueConfirm(false);
    localStorage.removeItem('nova_ts_session');
  }, []);

  const closeTsSession = useCallback(async (reason) => {
    if (!tsSessionId) return;
    try { await api.post(`/assistant/troubleshooting/${tsSessionId}/close`, { reason }); } catch (e) { /* non bloquant */ }
  }, [tsSessionId]);

  const onNewProblem = useCallback(() => {
    if (tsSession && !tsSession.terminal) { setShowNewIssueConfirm(true); return; }
    resetTs();
  }, [tsSession, resetTs]);

  const loadRecentSessions = useCallback(async () => {
    if (!isLoggedIn) return;
    try {
      const r = await api.get('/assistant/troubleshooting');
      setRecentSessions(r.data.items || []);
    } catch (e) { /* non bloquant */ }
  }, [isLoggedIn]);

  const confirmNewProblem = useCallback(async () => {
    // Suspend (conserve l'historique + reprise possible), n'abandonne pas.
    await closeTsSession('paused');
    const kept = tsSession ? {
      session_id: tsSessionId,
      problem: tsSession.problem || (tsSession.recap && tsSession.recap.problem),
      device: tsSession.device, application: tsSession.app,
    } : null;
    resetTs();
    if (kept && kept.session_id) setResumableReminder(kept);
    loadRecentSessions();
    setMessages((m) => [...m, { role: 'assistant', content: "D'accord, votre diagnostic précédent a été conservé. Décrivez votre nouveau problème ci-dessous et je m'en occupe." }]);
  }, [closeTsSession, resetTs, tsSession, tsSessionId, loadRecentSessions]);

  // Reprendre une session (avec gestion de conflit si une autre est active)
  const doResume = useCallback(async (sessionId) => {
    setTsLoading(true);
    try {
      const r = await api.post(`/assistant/troubleshooting/${sessionId}/resume`);
      applyTsPayload(r.data, { pushMessage: true });
      setResumableReminder(null);
      setShowRecent(false);
      setResumeConflict(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reprise impossible.');
    } finally {
      setTsLoading(false);
    }
  }, [applyTsPayload]);

  const resumeSession = useCallback((sessionId) => {
    if (tsSession && !tsSession.terminal && tsSessionId && tsSessionId !== sessionId) {
      setResumeConflict(sessionId);
      return;
    }
    doResume(sessionId);
  }, [tsSession, tsSessionId, doResume]);

  const confirmResumeConflict = useCallback(async () => {
    const target = resumeConflict;
    await closeTsSession('paused'); // suspend la session active (conservée)
    resetTs();
    if (target) await doResume(target);
  }, [resumeConflict, closeTsSession, resetTs, doResume]);

  const closeSessionPermanently = useCallback(async (sessionId) => {
    try { await api.post(`/assistant/troubleshooting/${sessionId}/close`, { reason: 'abandoned_by_user' }); }
    catch (e) { /* non bloquant */ }
    if (resumableReminder && resumableReminder.session_id === sessionId) setResumableReminder(null);
    loadRecentSessions();
  }, [resumableReminder, loadRecentSessions]);

  // Basculer vers le nouveau problème détecté automatiquement
  const handleSwitchNewIssue = useCallback(async () => {
    const pending = pendingNewIssueMsg;
    setPendingNewIssueMsg(null);
    await closeTsSession('replaced_by_new_issue');
    resetTs();
    if (pending) await startTroubleshooting(pending, { pushUser: false });
  }, [pendingNewIssueMsg, closeTsSession, resetTs, startTroubleshooting]);

  // Continuer le diagnostic actuel : le message est renvoyé comme réponse forcée à l'étape
  const handleKeepCurrent = useCallback(async () => {
    const pending = pendingNewIssueMsg;
    setPendingNewIssueMsg(null);
    if (pending) await tsAnswer(null, pending, false, null, true);
  }, [pendingNewIssueMsg, tsAnswer]);

  const fetchDeferredVideos = async (cid) => {
    try {
      const r = await api.post(`/assistant/conversation/${cid}/videos`);
      const vids = r.data.videos || [];
      setMessages((m) => {
        const c = [...m];
        for (let i = c.length - 1; i >= 0; i--) {
          if (c[i].role === 'assistant') { c[i] = { ...c[i], videos: vids, videoLoading: false }; break; }
        }
        return c;
      });
    } catch (e) {
      setMessages((m) => {
        const c = [...m];
        for (let i = c.length - 1; i >= 0; i--) {
          if (c[i].role === 'assistant') { c[i] = { ...c[i], videoLoading: false }; break; }
        }
        return c;
      });
    }
  };

  useEffect(() => {
    if (initialQuestion && !startedRef.current) {
      startedRef.current = true;
      sessionStorage.removeItem('nova_pending_intent');
      setMessages([welcomeMessage()]);
      sendMessage(initialQuestion);
    }
  }, [initialQuestion, sendMessage]);

  // Contrôles du dépannage guidé
  const onTsControl = (control, label) => tsAnswer(control, null, true, label);

  const onTsVideo = async () => {
    if (!tsSession || !tsSession.video_query) return;
    setTsLoading(true);
    try {
      const r = await api.post(`/assistant/troubleshooting/${tsSessionId}/video`, { query: tsSession.video_query });
      setTsVideos(r.data.videos || []);
      if (!(r.data.videos || []).length) toast.info("Aucune vidéo adaptée trouvée pour le moment.");
    } catch (e) {
      toast.error("Recherche vidéo indisponible.");
    } finally {
      setTsLoading(false);
    }
  };

  const copyTsRecap = () => {
    const r = (tsSession && tsSession.recap) || {};
    const lines = [
      'Récapitulatif — Nova (IPTV4KFR)',
      r.problem ? `Problème : ${r.problem}` : null,
      r.device ? `Appareil : ${r.device}` : null,
      r.application ? `Application : ${r.application}` : null,
      r.error_message ? `Message d'erreur : ${r.error_message}` : null,
      '',
      'Étapes réalisées :',
      ...(r.steps || []).map((s, i) => `${i + 1}. ${s.title}`),
      '',
      `Date : ${new Date().toLocaleString('fr-FR')}`,
    ].filter((l) => l !== null);
    navigator.clipboard.writeText(lines.join('\n')).then(
      () => toast.success('Récapitulatif copié.'),
      () => toast.error('Copie impossible.')
    );
  };

  // Reprise d'une session interrompue : NE PAS rouvrir automatiquement.
  // On affiche un rappel discret + on charge les diagnostics récents.
  useEffect(() => {
    if (initialQuestion || resumedRef.current) return;
    resumedRef.current = true;
    loadRecentSessions();
    const sid = localStorage.getItem('nova_ts_session');
    if (!sid) return;
    api.get(`/assistant/troubleshooting/${sid}`).then((r) => {
      const d = r.data;
      if (d && !d.terminal && d.status && !['resolved', 'unresolved', 'abandoned'].includes(d.status)) {
        setResumableReminder({
          session_id: sid, problem: (d.recap && d.recap.problem) || d.problem,
          device: d.device, application: d.app,
        });
      } else {
        localStorage.removeItem('nova_ts_session');
      }
    }).catch(() => localStorage.removeItem('nova_ts_session'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Abonnement actif (pour la confirmation technique lors d'une installation)
  useEffect(() => {
    if (!isLoggedIn) return;
    api.get('/assistant/my-subscriptions').then((r) => {
      const items = (r.data && r.data.items) || [];
      const withCreds = items.find((s) => s.has_credentials) || items[0];
      if (withCreds) setActiveSubId(withCreds.subscription_id);
    }).catch(() => {});
  }, [isLoggedIn]);

  const newConversation = () => {
    setConversationId(null);
    setDetected({});
    setMessages([buildWelcome()]);
    setShowHistoryMobile(false);
    setTsSession(null);
    setTsSessionId(null);
    setTsVideos([]);
    localStorage.removeItem('nova_ts_session');
  };

  const loadConversation = async (id) => {
    try {
      const r = await api.get(`/assistant/conversations/${id}`);
      const c = r.data;
      setConversationId(c.id);
      setDetected(c.context || {});
      setMessages((c.messages || []).map((m) => ({
        role: m.role, content: m.content, videos: m.videos || [],
        quick_replies: m.role === 'assistant' ? (m.quick_replies || []) : [],
        is_question: m.is_question,
        action: m.action || null,
      })));
      setShowHistoryMobile(false);
    } catch (e) { toast.error('Impossible de charger la conversation.'); }
  };

  const copyRecap = (msg) => {
    const solution = (msg?.content || '').replace(/\*\*/g, '');
    const lines = [
      'Récapitulatif — Nova (IPTV4KFR)',
      detected.problem ? `Problème : ${detected.problem}` : null,
      detected.device ? `Appareil : ${detected.device}` : null,
      detected.app ? `Application : ${detected.app}` : null,
      detected.connection ? `Connexion : ${detected.connection}` : null,
      '',
      'Solution :',
      solution,
      '',
      `Date de résolution : ${new Date().toLocaleString('fr-FR')}`,
    ].filter((l) => l !== null);
    navigator.clipboard.writeText(lines.join('\n')).then(
      () => toast.success('Récapitulatif copié.'),
      () => toast.error('Copie impossible.')
    );
  };

  const sendFeedback = async (type, videoId, msgIndex) => {
    try {
      await api.post('/assistant/feedback', {
        conversation_id: conversationId, feedback_type: type, video_id: videoId || null,
      });
      // Retour spécifique sur une réponse issue de la recherche IA (Phase 4.4-A)
      const msg = messages[msgIndex];
      if (msg && msg.researched_answer_id && (type === 'resolved' || type === 'not_resolved')) {
        api.post('/assistant/researched-answer/feedback', {
          researched_answer_id: msg.researched_answer_id, resolved: type === 'resolved',
        }).catch(() => {});
      }
      if (type === 'resolved') {
        toast.success("Parfait, ravie d'avoir pu vous aider !");
        setMessages((m) => m.map((msg, i) => i === msgIndex ? { ...msg, resolved: true } : msg));
        loadHistory();
      } else if (type === 'not_resolved') {
        toast.info("Continuons le diagnostic. Donnez-moi un peu plus de détails.");
        setMessages((m) => m.map((msg, i) => i === msgIndex ? { ...msg, resolved: false } : msg));
      }
    } catch (e) { toast.error("Impossible d'enregistrer votre retour."); }
  };

  const HistoryPanel = () => (
    <div className="h-full flex flex-col bg-[#0A0A0B] border-r border-[#3A3A3D] w-64">
      <div className="p-3 border-b border-[#3A3A3D]">
        <button
          onClick={newConversation}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white text-sm font-medium hover:shadow-lg hover:shadow-[#FF6B35]/30 transition-all"
          data-testid="new-conversation-btn"
        >
          <Plus className="h-4 w-4" /> Nouvelle conversation
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {history.length === 0 && <p className="text-[#6B6B6E] text-xs p-2">Aucune conversation.</p>}
        {history.map((c) => (
          <button
            key={c.id}
            onClick={() => loadConversation(c.id)}
            className={`w-full text-left p-2 rounded-lg text-xs transition-colors ${conversationId === c.id ? 'bg-[#FF6B35]/15 text-white' : 'text-[#B8B8BA] hover:bg-[#1A1A1D]'}`}
            data-testid="history-item"
          >
            <p className="font-medium truncate">{c.title || 'Conversation'}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[10px] text-[#6B6B6E]">{c.updated_at ? new Date(c.updated_at).toLocaleDateString('fr-FR') : ''}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${c.status === 'resolved' ? 'bg-[#00FF88]/15 text-[#00FF88]' : 'bg-[#FFB800]/15 text-[#FFB800]'}`}>
                {c.status === 'resolved' ? 'Résolu' : 'En cours'}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="flex h-full" data-testid="assistant-chat">
      {/* Historique (desktop, connecté) — masqué en mode compact (bulle) */}
      {!compact && isLoggedIn && <div className="hidden md:block"><HistoryPanel /></div>}
      {/* Historique (mobile, overlay) */}
      {!compact && isLoggedIn && showHistoryMobile && (
        <div className="absolute inset-0 z-30 flex md:hidden">
          <HistoryPanel />
          <div className="flex-1 bg-black/50" onClick={() => setShowHistoryMobile(false)} />
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {/* En-tête Nova — masqué en mode compact (la bulle a déjà son en-tête) */}
        {!compact && (
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#3A3A3D] bg-[#0A0A0B]">
          {isLoggedIn && (
            <button onClick={() => setShowHistoryMobile(true)} className="md:hidden text-[#B8B8BA]" data-testid="history-toggle">
              <History className="h-5 w-5" />
            </button>
          )}
          <NovaAvatar size={40} />
          <div className="flex-1 min-w-0">
            <p className="text-white font-bold leading-tight">Nova</p>
            <p className="text-[#6B6B6E] text-xs">Assistante Support IPTV4KFR</p>
          </div>
          <div className="flex items-center gap-3">
            {isLoggedIn && (
              <button onClick={() => { setShowRecent((v) => !v); loadRecentSessions(); }} data-testid="ts-recent-toggle"
                className="flex items-center gap-1 text-[11px] text-[#B8B8BA] hover:text-[#FF6B35]">
                <History className="h-3.5 w-3.5" /> Diagnostics récents
              </button>
            )}
            <div className="flex items-center gap-1.5 text-xs text-[#00FF88]">
              <span className="w-2 h-2 rounded-full bg-[#00FF88] animate-pulse" /> En ligne
            </div>
          </div>
        </div>
        )}

        {/* Barre compacte : nouvelle conversation */}
        {compact && isLoggedIn && (
          <div className="flex justify-between px-3 py-1.5 border-b border-[#3A3A3D] bg-[#0A0A0B]">
            <button onClick={() => { setShowRecent((v) => !v); loadRecentSessions(); }} className="flex items-center gap-1 text-[11px] text-[#B8B8BA] hover:text-[#FF6B35]" data-testid="ts-recent-toggle-compact">
              <History className="h-3.5 w-3.5" /> Récents
            </button>
            <button onClick={newConversation} className="flex items-center gap-1 text-[11px] text-[#B8B8BA] hover:text-[#FF6B35]" data-testid="new-conversation-btn">
              <Plus className="h-3.5 w-3.5" /> Nouvelle conversation
            </button>
          </div>
        )}

        {/* Progression du dépannage guidé */}
        {tsSession && tsSession.progress && tsSession.progress.length > 0 && (
          <TroubleshootingProgress progress={tsSession.progress} />
        )}

        {/* Barre « Nouveau problème » (dépannage en cours) */}
        {tsSession && !tsSession.terminal && (
          <div className="flex items-center justify-between px-4 py-1.5 border-b border-[#3A3A3D] bg-[#0A0A0B]">
            <span className="text-[10px] text-[#6B6B6E]">Diagnostic en cours</span>
            <button onClick={onNewProblem} data-testid="ts-new-problem-btn"
              className="flex items-center gap-1 text-[11px] text-[#B8B8BA] hover:text-[#FF6B35] transition-colors">
              <RefreshCw className="h-3.5 w-3.5" /> Nouveau problème
            </button>
          </div>
        )}
        {showNewIssueConfirm && (
          <div className="px-4 py-2.5 border-b border-[#FF6B35]/30 bg-[#FF6B35]/5" data-testid="ts-new-problem-confirm">
            <p className="text-xs text-[#E8E8E8] mb-2">Un diagnostic est en cours. Voulez-vous le suspendre et démarrer un nouveau problème ?</p>
            <div className="flex flex-wrap gap-2">
              <button onClick={confirmNewProblem} data-testid="ts-new-problem-yes"
                className="text-xs px-3 py-1.5 rounded-lg bg-[#FF6B35] text-white hover:opacity-90">
                Oui, démarrer un nouveau problème
              </button>
              <button onClick={() => setShowNewIssueConfirm(false)} data-testid="ts-new-problem-no"
                className="text-xs px-3 py-1.5 rounded-lg bg-[#1A1A1D] border border-[#3A3A3D] text-[#B8B8BA] hover:text-white">
                Non, continuer le dépannage actuel
              </button>
            </div>
          </div>
        )}

        {/* Rappel discret : diagnostic conservé (Part 2) */}
        {resumableReminder && !tsSession && (
          <div className="px-4 py-2.5 border-b border-[#00FFFF]/25 bg-[#00FFFF]/5 flex items-center justify-between gap-2" data-testid="ts-resume-reminder">
            <span className="text-xs text-[#B8B8BA]">
              Votre diagnostic précédent{resumableReminder.problem ? ` (${resumableReminder.problem})` : ''} a été conservé.
            </span>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => resumeSession(resumableReminder.session_id)} data-testid="ts-resume-reminder-btn"
                className="text-[11px] px-2.5 py-1 rounded-lg bg-[#00FFFF]/15 text-[#00FFFF] border border-[#00FFFF]/40 hover:bg-[#00FFFF]/25">
                Reprendre le diagnostic précédent
              </button>
              <button onClick={() => setResumableReminder(null)} data-testid="ts-resume-reminder-dismiss"
                className="text-[11px] px-2 py-1 rounded-lg text-[#6B6B6E] hover:text-white">Ignorer</button>
            </div>
          </div>
        )}

        {/* Conflit : reprendre alors qu'une session est active */}
        {resumeConflict && (
          <div className="px-4 py-2.5 border-b border-[#FFB800]/30 bg-[#FFB800]/5" data-testid="ts-resume-conflict">
            <p className="text-xs text-[#E8E8E8] mb-2">Un autre diagnostic est déjà en cours. Souhaitez-vous le suspendre pour reprendre celui-ci ?</p>
            <div className="flex flex-wrap gap-2">
              <button onClick={confirmResumeConflict} data-testid="ts-resume-conflict-yes"
                className="text-xs px-3 py-1.5 rounded-lg bg-[#FF6B35] text-white hover:opacity-90">Oui, suspendre et reprendre</button>
              <button onClick={() => setResumeConflict(null)} data-testid="ts-resume-conflict-no"
                className="text-xs px-3 py-1.5 rounded-lg bg-[#1A1A1D] border border-[#3A3A3D] text-[#B8B8BA] hover:text-white">Annuler</button>
            </div>
          </div>
        )}

        {/* Panneau : diagnostics récents */}
        {showRecent && (
          <div className="px-4 py-3 border-b border-[#3A3A3D] bg-[#0A0A0B]" data-testid="ts-recent-panel">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-white">Diagnostics récents</p>
              <button onClick={() => setShowRecent(false)} className="text-[11px] text-[#6B6B6E] hover:text-white">Fermer</button>
            </div>
            {recentSessions.length === 0 && <p className="text-[11px] text-[#6B6B6E]">Aucun diagnostic récent.</p>}
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {recentSessions.map((s) => (
                <div key={s.session_id} className="rounded-lg border border-[#3A3A3D] p-2.5" data-testid="ts-recent-item">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-white font-medium truncate">{s.problem || 'Diagnostic'}</p>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${s.status === 'resolved' ? 'bg-[#00FF88]/15 text-[#00FF88]' : s.active || s.status === 'paused' ? 'bg-[#FFB800]/15 text-[#FFB800]' : 'bg-[#3A3A3D] text-[#B8B8BA]'}`}>
                      {s.status_label}
                    </span>
                  </div>
                  <p className="text-[10px] text-[#6B6B6E] mt-0.5">
                    {[s.device, s.application].filter(Boolean).join(' · ') || 'Appareil non précisé'}
                    {s.date ? ` · ${new Date(s.date).toLocaleDateString('fr-FR')}` : ''}
                    {s.last_step_title ? ` · Dernière étape : ${s.last_step_title}` : ''}
                  </p>
                  <div className="mt-1 h-1 rounded-full bg-[#1A1A1D] overflow-hidden">
                    <div className="h-full bg-[#FF6B35]" style={{ width: `${s.progress_percent}%` }} />
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {s.resumable && (
                      <button onClick={() => resumeSession(s.session_id)} data-testid="ts-recent-resume"
                        className="text-[11px] px-2.5 py-1 rounded-lg bg-[#00FFFF]/15 text-[#00FFFF] border border-[#00FFFF]/40">Reprendre</button>
                    )}
                    {!s.resumable && (
                      <span className="text-[10px] text-[#6B6B6E] italic self-center">Terminé</span>
                    )}
                    <button onClick={() => closeSessionPermanently(s.session_id)} data-testid="ts-recent-close"
                      className="text-[11px] px-2.5 py-1 rounded-lg border border-[#3A3A3D] text-[#B8B8BA] hover:text-white">Fermer définitivement</button>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => { setShowRecent(false); onNewProblem(); }} data-testid="ts-recent-new"
              className="mt-2 text-[11px] px-2.5 py-1 rounded-lg bg-[#FF6B35] text-white">Démarrer un nouveau problème</button>
          </div>
        )}

        {/* Messages */}
        <div ref={scrollRef} aria-live="polite" aria-atomic="false" className="flex-1 overflow-y-auto px-4 py-6 space-y-5">
          {messages.map((msg, i) => (
            <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''} max-w-3xl mx-auto`}>
              {msg.role === 'user'
                ? <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#00FFFF]/15 flex items-center justify-center text-[#00FFFF] text-xs font-bold">Vous</div>
                : <NovaAvatar size={32} />}
              <div className={`flex-1 min-w-0 ${msg.role === 'user' ? 'text-right' : ''}`}>
                <div className={`inline-block text-left rounded-2xl px-4 py-3 text-sm ${msg.role === 'user' ? 'bg-[#00FFFF]/10 text-white' : 'bg-[#1A1A1D] border border-[#3A3A3D] text-[#E8E8E8]'}`}>
                  {msg.role === 'assistant' ? renderRich(msg.content) : msg.content}
                </div>

                {/* Sources officielles (mention discrète — uniquement si fiche référencée avec source officielle valide) */}
                {msg.role === 'assistant' && msg.official_sources && msg.official_sources.show && (
                  <div className="mt-1.5" data-testid="official-sources">
                    <p className="text-[11px] text-[#6B6B6E] italic">
                      {msg.official_sources.mention}{' '}
                      <button onClick={() => setOpenSourcesIdx(openSourcesIdx === i ? null : i)}
                        className="not-italic text-[#00FFFF] hover:underline" data-testid="view-sources-btn">
                        Voir les sources
                      </button>
                    </p>
                    {openSourcesIdx === i && (
                      <div className="mt-1.5 rounded-lg border border-[#00FFFF]/25 bg-[#00FFFF]/5 p-2.5 space-y-1.5 max-w-md" data-testid="official-sources-panel">
                        {(msg.official_sources.sources || []).map((s, k) => (
                          <div key={k} className="text-[11px]" data-testid="official-source-item">
                            <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-[#00FFFF] hover:underline font-medium">{s.title || s.domain}</a>
                            <p className="text-[10px] text-[#6B6B6E]">{s.domain} · {s.type}{s.verified_at ? ` · vérifiée le ${new Date(s.verified_at).toLocaleDateString('fr-FR')}` : ''}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Réponses rapides */}
                {msg.role === 'assistant' && msg.quick_replies && msg.quick_replies.length > 0 && i === messages.length - 1 && !loading && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {msg.quick_replies.map((qr, k) => (
                      <button
                        key={k}
                        onClick={() => sendMessage(qr)}
                        className="text-xs px-3 py-2 rounded-lg bg-[#1A1A1D] border border-[#3A3A3D] text-[#E8E8E8] hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors"
                        data-testid="quick-reply"
                      >
                        {qr}
                      </button>
                    ))}
                  </div>
                )}

                {/* Choix de bascule vers un nouveau problème détecté */}
                {msg.newIssueChoices && i === messages.length - 1 && (
                  <div className="flex flex-wrap gap-2 mt-3" data-testid="ts-new-issue-choices">
                    <button onClick={handleSwitchNewIssue} data-testid="ts-new-issue-yes"
                      className="text-xs px-3 py-2 rounded-lg bg-[#FF6B35] text-white hover:opacity-90 transition-colors">
                      Oui, ouvrir le nouveau problème
                    </button>
                    <button onClick={handleKeepCurrent} data-testid="ts-new-issue-no"
                      className="text-xs px-3 py-2 rounded-lg bg-[#1A1A1D] border border-[#3A3A3D] text-[#E8E8E8] hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                      Non, continuer le diagnostic actuel
                    </button>
                  </div>
                )}

                {/* Vidéos */}
                {msg.videos && msg.videos.length > 0 && (
                  <div className="mt-3 space-y-3">
                    {msg.videos.map((v) => (
                      <div key={v.video_id}>
                        <YouTubePlayer video={v} primary={v.is_primary} onFeedback={(t, id) => sendFeedback(t, id, i)} />
                        <VideoValidation videoId={v.video_id} context={{
                          device: detected.device, application: detected.app,
                          problem: detected.problem || detected.category, language: 'fr',
                        }} />
                      </div>
                    ))}
                  </div>
                )}

                {/* Recherche vidéo différée (non bloquante) */}
                {msg.videoLoading && (
                  <div className="mt-3 flex items-center gap-2 text-[#B8B8BA] text-xs" data-testid="video-loading">
                    <Loader2 className="h-3.5 w-3.5 text-[#FF6B35] animate-spin" />
                    Nova recherche un tutoriel vidéo adapté à votre appareil…
                  </div>
                )}

                {/* Action sécurisée (identifiants / souscription / statut) — dernier message uniquement */}
                {msg.role === 'assistant' && msg.action && msg.action.type && i === messages.length - 1 && !loading && (
                  <NovaActionZone action={msg.action} isLoggedIn={isLoggedIn} onNavigate={handleNavigate} />
                )}

                {/* Retour résolution */}
                {msg.awaiting_resolution && msg.resolved === undefined && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className="text-[#6B6B6E] text-xs">Cela a-t-il résolu votre problème ?</span>
                    <button onClick={() => sendFeedback('resolved', null, i)} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-[#00FF88]/15 text-[#00FF88] hover:bg-[#00FF88]/25 transition-colors" data-testid="feedback-resolved">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Oui, mon problème est résolu
                    </button>
                    <button onClick={() => sendFeedback('not_resolved', null, i)} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-[#FF3B5C]/15 text-[#FF3B5C] hover:bg-[#FF3B5C]/25 transition-colors" data-testid="feedback-not-resolved">
                      <XCircle className="h-3.5 w-3.5" /> Non, le problème persiste
                    </button>
                  </div>
                )}
                {msg.resolved === true && (
                  <div className="mt-3 rounded-xl border border-[#00FF88]/30 bg-[#00FF88]/5 p-3" data-testid="resolution-summary">
                    <p className="text-[#00FF88] text-sm font-semibold mb-2">✓ Votre problème est indiqué comme résolu.</p>
                    <div className="text-xs text-[#B8B8BA] space-y-0.5">
                      {detected.problem && <p><span className="text-[#6B6B6E]">Problème :</span> {detected.problem}</p>}
                      {detected.device && <p><span className="text-[#6B6B6E]">Appareil :</span> {detected.device}</p>}
                      {detected.app && <p><span className="text-[#6B6B6E]">Application :</span> {detected.app}</p>}
                      <p><span className="text-[#6B6B6E]">Date :</span> {new Date().toLocaleDateString('fr-FR')}</p>
                    </div>
                    <button
                      onClick={() => copyRecap(msg)}
                      className="mt-2 flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-[#1A1A1D] border border-[#3A3A3D] text-white hover:border-[#00FF88] transition-colors"
                      data-testid="copy-recap-btn"
                    >
                      <Copy className="h-3.5 w-3.5" /> Copier le récapitulatif
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Zone interactive du dépannage guidé (étape courante ou terminale) */}
          {tsSession && (
            <div className="max-w-3xl mx-auto w-full">
              <TroubleshootingControls
                session={tsSession}
                loading={tsLoading}
                videos={tsVideos}
                onAnswer={(value, label) => tsAnswer(value, null, true, label)}
                onControl={onTsControl}
                onVideo={onTsVideo}
                onNavigate={handleNavigate}
                isLoggedIn={isLoggedIn}
                onCopyRecap={copyTsRecap}
              />
            </div>
          )}

          {loading && (
            <div className="flex gap-3 max-w-3xl mx-auto">
              <NovaAvatar size={32} />
              <div className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-2xl px-4 py-3 flex items-center gap-2">
                <Loader2 className="h-4 w-4 text-[#FF6B35] animate-spin" />
                <span className="text-[#B8B8BA] text-sm">
                  Nova recherche la meilleure solution…
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Contexte détecté */}
        {(detected.device || detected.app || detected.problem) && (
          <div className="px-4 py-2 border-t border-[#3A3A3D]/50 flex flex-wrap gap-2 max-w-3xl mx-auto w-full">
            {detected.device && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#00FFFF]/10 text-[#00FFFF]">📺 {detected.device}</span>}
            {detected.app && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FF6B35]/10 text-[#FF6B35]">📱 {detected.app}</span>}
            {detected.category && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#1A1A1D] text-[#B8B8BA]">{detected.category}</span>}
          </div>
        )}

        {/* Saisie */}
        <div className="p-4 border-t border-[#3A3A3D] bg-[#0A0A0B]">
          <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex items-end gap-2 max-w-3xl mx-auto">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              rows={1}
              placeholder="Décrivez votre problème…"
              className="flex-1 resize-none bg-[#1A1A1D] border border-[#3A3A3D] rounded-xl px-4 py-3 text-sm text-white placeholder-[#6B6B6E] focus:border-[#FF6B35] focus:outline-none"
              data-testid="assistant-input"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white flex items-center justify-center disabled:opacity-40 hover:shadow-lg hover:shadow-[#FF6B35]/30 transition-all"
              aria-label="Envoyer votre message à Nova"
              data-testid="assistant-send"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
            </button>
          </form>
          {!ytConfigured && (
            <p className="text-[10px] text-[#6B6B6E] text-center mt-2 max-w-3xl mx-auto">
              Les recommandations vidéo s'activeront prochainement.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssistantChat;
